import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";

import { Lead } from "0.Mine/3.Interfaces/lead";
import {
  SafeArgonBox as ArgonBox,
  SafeArgonButton,
  SafeArgonInput,
} from "0.Mine/4.Argon/ArgonComponents";

interface EmailModalProps {
  open: boolean;
  onClose: () => void;
  lead: Lead | null;
}

const EmailModal: React.FC<EmailModalProps> = ({ open, onClose, lead }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [showCcBcc, setShowCcBcc] = useState(false);

  // Email state
  const [emailData, setEmailData] = useState({
    to: lead?.email || "",
    subject: "",
    message: "",
    template: "none",
    cc: "",
    bcc: "",
  });

  // Sample email templates
  const emailTemplates = [
    { id: "none", name: "No Template" },
    { id: "intro", name: "Introduction" },
    { id: "follow-up", name: "Follow-up" },
    { id: "proposal", name: "Proposal" },
    { id: "meeting", name: "Meeting Request" },
  ];

  // Update email form data
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEmailData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle template selection
  const handleTemplateChange = (e: any) => {
    const templateId = e.target.value;
    setEmailData((prev) => ({ ...prev, template: templateId }));

    // Apply template content if a real template is selected
    if (templateId !== "none") {
      // These would normally be loaded from a template system
      const templates: Record<string, { subject: string; body: string }> = {
        intro: {
          subject: `Introduction - ${lead?.company || "Our Company"}`,
          body: `Dear ${
            lead?.name || "Valued Customer"
          },\n\nThank you for your interest in our services. I'd like to introduce myself as your dedicated account manager.\n\nCould we schedule a brief call to discuss your needs?\n\nBest regards,\n[Your Name]`,
        },
        "follow-up": {
          subject: "Following up on our conversation",
          body: `Hello ${
            lead?.name || "there"
          },\n\nI wanted to follow up on our previous conversation about ${
            lead?.company || "your company"
          }'s needs.\n\nHave you had a chance to review the information I sent?\n\nBest regards,\n[Your Name]`,
        },
        proposal: {
          subject: "Proposal for our services",
          body: `Dear ${
            lead?.name || "Valued Customer"
          },\n\nAttached please find our proposal for ${
            lead?.company || "your company"
          }.\n\nPlease review and let me know if you have any questions.\n\nBest regards,\n[Your Name]`,
        },
        meeting: {
          subject: "Request for a meeting",
          body: `Dear ${
            lead?.name || "Valued Customer"
          },\n\nI would like to schedule a meeting to discuss how our services can help ${
            lead?.company || "your company"
          }.\n\nWould you be available next week?\n\nBest regards,\n[Your Name]`,
        },
      };

      const template = templates[templateId];
      if (template) {
        setEmailData((prev) => ({
          ...prev,
          subject: template.subject,
          message: template.body,
        }));
      }
    }
  };

  // Send email function
  const handleSendEmail = () => {
    console.log("Sending email:", emailData);
    // Implement actual email sending logic here
    // Reset form and close modal after sending
    setEmailData({
      to: lead?.email || "",
      subject: "",
      message: "",
      template: "none",
      cc: "",
      bcc: "",
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          minHeight: isMobile ? "80vh" : "60vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          background: "#494b95",
          mb: 2,
          pb: 1,
        }}
      >
        <Typography variant="h6">
          {lead?.name ? `Email to ${lead.name}` : "Compose Email"}
        </Typography>
        <IconButton
          edge="end"
          onClick={onClose}
          sx={{ marginTop: "-5px", color: "#ffffff!important" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SafeArgonInput
              label="To"
              name="to"
              value={emailData.to}
              onChange={handleInputChange}
              fullWidth
              placeholder="email@mail.com"
            />

            <Typography
              variant="body2"
              onClick={() => setShowCcBcc((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                color: "primary.main",
                cursor: "pointer",
                mt: 1,
                fontSize: "13px",
                marginLeft: "9px",
                marginBottom: "0px",
              }}
            >
              {showCcBcc ? "Nascondi CC/BCC" : "Mostra CC/BCC"}
              {showCcBcc ? (
                <ExpandLessIcon sx={{ ml: 0.5 }} />
              ) : (
                <ExpandMoreIcon sx={{ ml: 0.5 }} />
              )}
            </Typography>
          </Grid>

          {showCcBcc && (
            <>
              <Grid item xs={12} sm={6}>
                <SafeArgonInput
                  label="CC"
                  name="cc"
                  value={emailData.cc}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="copiaconoscenza@mail.com"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <SafeArgonInput
                  label="BCC"
                  name="bcc"
                  value={emailData.bcc}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="conoscenzanascosta@mail.com"
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={8}>
            <SafeArgonInput
              label="Subject"
              name="subject"
              value={emailData.subject}
              onChange={handleInputChange}
              fullWidth
              placeholder="Oggetto email"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="email-template-label">Template</InputLabel>
              <Select
                labelId="email-template-label"
                id="email-template"
                value={emailData.template}
                onChange={handleTemplateChange}
                label="Template"
              >
                {emailTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <SafeArgonInput
              multiline
              rows={10}
              label="Message"
              name="message"
              value={emailData.message}
              onChange={handleInputChange}
              fullWidth
              placeholder="Scrivi il tuo messaggio qui..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "inherit",
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          justifyContent: "space-between",
        }}
      >
        <ArgonBox>
          <SafeArgonButton
            variant="outlined"
            color="primary"
            startIcon={<AttachFileIcon />}
            sx={{ mr: 1 }}
          >
            Allega
          </SafeArgonButton>
        </ArgonBox>

        <ArgonBox>
          <SafeArgonButton variant="outlined" color="secondary" onClick={onClose} sx={{ mr: 1 }}>
            Annulla
          </SafeArgonButton>
          <SafeArgonButton
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={handleSendEmail}
            disabled={!emailData.to || !emailData.subject || !emailData.message}
          >
            Invia
          </SafeArgonButton>
        </ArgonBox>
      </DialogActions>
    </Dialog>
  );
};

export default EmailModal;
